import React, { useState } from 'react';
import { AwardIcon, CreditCardIcon, GiftIcon, TrendingUpIcon, TagIcon, FileTextIcon, DollarSignIcon, ReceiptIcon, ShieldIcon, RefreshCwIcon, DownloadIcon, CheckCircleIcon, ClockIcon, AlertCircleIcon } from 'lucide-react';
interface LoyaltyBillingProps {
  patientId: number;
}
export function LoyaltyBilling({
  patientId
}: LoyaltyBillingProps) {
  const [activeSection, setActiveSection] = useState<'loyalty' | 'billing' | 'insurance' | 'payments' | 'invoices'>('loyalty');
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [selectedPaymentMode, setSelectedPaymentMode] = useState('');
  const loyaltyData = {
    points: 1250,
    tier: 'Gold',
    nextTier: 'Platinum',
    pointsToNextTier: 750,
    memberSince: '2020-03-15',
    totalVisits: 42,
    lifetimeValue: 15420
  };
  const activePackages = [{
    id: 1,
    name: 'Annual Wellness Package',
    price: 5000,
    discount: 20,
    validUntil: '2024-12-31',
    servicesUsed: 3,
    totalServices: 12
  }, {
    id: 2,
    name: 'Dental Care Package',
    price: 3000,
    discount: 15,
    validUntil: '2024-06-30',
    servicesUsed: 1,
    totalServices: 6
  }];
  const availableDiscounts = [{
    id: 1,
    name: 'Senior Citizen Discount',
    value: '10%',
    applicable: 'All consultations',
    status: 'Active'
  }, {
    id: 2,
    name: 'Loyalty Member Discount',
    value: '15%',
    applicable: 'Lab tests',
    status: 'Active'
  }, {
    id: 3,
    name: 'Family Package Discount',
    value: '20%',
    applicable: 'All services',
    status: 'Inactive'
  }];
  const rewardsHistory = [{
    id: 1,
    date: '2024-01-10',
    action: 'Appointment completed',
    points: 50,
    balance: 1250
  }, {
    id: 2,
    date: '2023-12-15',
    action: 'Referral bonus',
    points: 200,
    balance: 1200
  }, {
    id: 3,
    date: '2023-11-20',
    action: 'Package purchase',
    points: 300,
    balance: 1000
  }];
  const billingRules = [{
    id: 1,
    rule: 'Automatic discount on 5th visit',
    status: 'Active',
    discount: '10%'
  }, {
    id: 2,
    rule: 'Birthday month special',
    status: 'Active',
    discount: '15%'
  }, {
    id: 3,
    rule: 'Referral program',
    status: 'Active',
    discount: '₹500 credit'
  }];
  // Billing Data
  const currentBill = {
    billNumber: 'INV-2024-0156',
    date: '2024-01-15',
    status: 'Pending',
    items: [{
      category: 'Consultation',
      description: 'Dr. Sarah Johnson - Cardiology',
      quantity: 1,
      rate: 1500,
      amount: 1500,
      tax: 270
    }, {
      category: 'Lab',
      description: 'Complete Blood Count',
      quantity: 1,
      rate: 800,
      amount: 800,
      tax: 144
    }, {
      category: 'Lab',
      description: 'Lipid Profile',
      quantity: 1,
      rate: 1200,
      amount: 1200,
      tax: 216
    }, {
      category: 'Drugs',
      description: 'Lisinopril 10mg (30 tablets)',
      quantity: 1,
      rate: 450,
      amount: 450,
      tax: 81
    }, {
      category: 'Consumables',
      description: 'Syringes and bandages',
      quantity: 1,
      rate: 150,
      amount: 150,
      tax: 27
    }, {
      category: 'Imaging',
      description: 'Chest X-Ray',
      quantity: 1,
      rate: 1200,
      amount: 1200,
      tax: 216
    }],
    subtotal: 5300,
    discount: 530,
    taxTotal: 954,
    total: 5724
  };
  const billingHistory = [{
    id: 1,
    billNumber: 'INV-2024-0156',
    date: '2024-01-15',
    amount: 5724,
    status: 'Pending',
    paymentMode: '-'
  }, {
    id: 2,
    billNumber: 'INV-2024-0142',
    date: '2024-01-10',
    amount: 3250,
    status: 'Paid',
    paymentMode: 'UPI'
  }, {
    id: 3,
    billNumber: 'INV-2023-0998',
    date: '2023-12-15',
    amount: 4800,
    status: 'Paid',
    paymentMode: 'Card'
  }, {
    id: 4,
    billNumber: 'INV-2023-0876',
    date: '2023-11-20',
    amount: 2100,
    status: 'Refunded',
    paymentMode: 'Card'
  }];
  // Insurance Data
  const insuranceClaims = [{
    id: 1,
    claimNumber: 'CLM-2024-001',
    billNumber: 'INV-2024-0142',
    date: '2024-01-10',
    amount: 3250,
    claimedAmount: 2600,
    status: 'Approved',
    provider: 'Blue Cross Blue Shield',
    approvalDate: '2024-01-12'
  }, {
    id: 2,
    claimNumber: 'CLM-2023-089',
    billNumber: 'INV-2023-0998',
    date: '2023-12-15',
    amount: 4800,
    claimedAmount: 3840,
    status: 'Processing',
    provider: 'Blue Cross Blue Shield',
    approvalDate: '-'
  }];
  const paymentModes = [{
    id: 'card',
    name: 'Credit/Debit Card',
    icon: CreditCardIcon
  }, {
    id: 'upi',
    name: 'UPI',
    icon: DollarSignIcon
  }, {
    id: 'wallet',
    name: 'Digital Wallet',
    icon: DollarSignIcon
  }, {
    id: 'pos',
    name: 'POS Terminal',
    icon: CreditCardIcon
  }, {
    id: 'bank',
    name: 'Bank Transfer',
    icon: DollarSignIcon
  }, {
    id: 'invoice',
    name: 'Generate Invoice',
    icon: FileTextIcon
  }];
  // Refunds and Credit Notes
  const refundsCreditNotes = [{
    id: 1,
    type: 'Refund',
    referenceNumber: 'REF-2023-045',
    billNumber: 'INV-2023-0876',
    date: '2023-11-25',
    amount: 2100,
    status: 'Completed',
    reason: 'Service cancellation'
  }, {
    id: 2,
    type: 'Credit Note',
    referenceNumber: 'CN-2023-012',
    billNumber: 'INV-2023-0654',
    date: '2023-10-10',
    amount: 500,
    status: 'Applied',
    reason: 'Billing error adjustment'
  }];
  const deferredPayments = [{
    id: 1,
    billNumber: 'INV-2024-0120',
    originalAmount: 8500,
    paidAmount: 4000,
    remainingAmount: 4500,
    dueDate: '2024-02-15',
    status: 'Active'
  }];
  const handleProcessPayment = () => {
    if (!selectedPaymentMode) {
      alert('Please select a payment mode');
      return;
    }
    alert(`Processing payment of ₹${currentBill.total} via ${paymentModes.find(p => p.id === selectedPaymentMode)?.name}`);
    setShowPaymentModal(false);
  };
  const handleInitiateInsuranceClaim = () => {
    alert('Insurance claim initiated. Claim will be submitted to Blue Cross Blue Shield in standard format.');
  };
  return <div className="space-y-6" data-id="element-809">
      {/* Section Tabs */}
      <div className="flex flex-wrap gap-2 border-b border-gray-200 pb-4" data-id="element-810">
        <button onClick={() => setActiveSection('loyalty')} className={`px-4 py-2 rounded-lg font-medium transition-colors ${activeSection === 'loyalty' ? 'bg-yellow-100 text-yellow-700' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`} data-id="element-811">
          <AwardIcon className="w-4 h-4 inline mr-2" data-id="element-812" />
          Loyalty & Rewards
        </button>
        <button onClick={() => setActiveSection('billing')} className={`px-4 py-2 rounded-lg font-medium transition-colors ${activeSection === 'billing' ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`} data-id="element-813">
          <ReceiptIcon className="w-4 h-4 inline mr-2" data-id="element-814" />
          Billing & Checkout
        </button>
        <button onClick={() => setActiveSection('insurance')} className={`px-4 py-2 rounded-lg font-medium transition-colors ${activeSection === 'insurance' ? 'bg-purple-100 text-purple-700' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`} data-id="element-815">
          <ShieldIcon className="w-4 h-4 inline mr-2" data-id="element-816" />
          Insurance Claims
        </button>
        <button onClick={() => setActiveSection('payments')} className={`px-4 py-2 rounded-lg font-medium transition-colors ${activeSection === 'payments' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`} data-id="element-817">
          <DollarSignIcon className="w-4 h-4 inline mr-2" data-id="element-818" />
          Payments & Refunds
        </button>
        <button onClick={() => setActiveSection('invoices')} className={`px-4 py-2 rounded-lg font-medium transition-colors ${activeSection === 'invoices' ? 'bg-indigo-100 text-indigo-700' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`} data-id="element-819">
          <FileTextIcon className="w-4 h-4 inline mr-2" data-id="element-820" />
          Invoices & Tax
        </button>
      </div>
      {/* Loyalty & Rewards Section */}
      {activeSection === 'loyalty' && <>
          {/* Loyalty Overview */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-id="element-821">
            <div className="bg-gradient-to-br from-yellow-400 to-yellow-600 rounded-lg p-6 text-white" data-id="element-822">
              <div className="flex items-center justify-between mb-4" data-id="element-823">
                <AwardIcon className="w-8 h-8" data-id="element-824" />
                <span className="text-2xl font-bold" data-id="element-825">{loyaltyData.tier}</span>
              </div>
              <div className="text-sm opacity-90 mb-2" data-id="element-826">Loyalty Points</div>
              <div className="text-3xl font-bold" data-id="element-827">{loyaltyData.points}</div>
              <div className="mt-4 text-sm" data-id="element-828">
                {loyaltyData.pointsToNextTier} points to {loyaltyData.nextTier}
              </div>
            </div>
            <div className="bg-white border border-gray-200 rounded-lg p-6" data-id="element-829">
              <div className="flex items-center space-x-2 mb-4" data-id="element-830">
                <TrendingUpIcon className="w-6 h-6 text-blue-600" data-id="element-831" />
                <span className="font-semibold text-gray-900" data-id="element-832">
                  Patient Stats
                </span>
              </div>
              <div className="space-y-2" data-id="element-833">
                <div className="flex justify-between text-sm" data-id="element-834">
                  <span className="text-gray-600" data-id="element-835">Total Visits</span>
                  <span className="font-semibold" data-id="element-836">
                    {loyaltyData.totalVisits}
                  </span>
                </div>
                <div className="flex justify-between text-sm" data-id="element-837">
                  <span className="text-gray-600" data-id="element-838">Member Since</span>
                  <span className="font-semibold" data-id="element-839">
                    {loyaltyData.memberSince}
                  </span>
                </div>
                <div className="flex justify-between text-sm" data-id="element-840">
                  <span className="text-gray-600" data-id="element-841">Lifetime Value</span>
                  <span className="font-semibold" data-id="element-842">
                    ₹{loyaltyData.lifetimeValue.toLocaleString()}
                  </span>
                </div>
              </div>
            </div>
            <div className="bg-white border border-gray-200 rounded-lg p-6" data-id="element-843">
              <div className="flex items-center space-x-2 mb-4" data-id="element-844">
                <GiftIcon className="w-6 h-6 text-green-600" data-id="element-845" />
                <span className="font-semibold text-gray-900" data-id="element-846">Rewards</span>
              </div>
              <div className="space-y-2" data-id="element-847">
                <button className="w-full px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 text-sm" data-id="element-848">
                  Redeem Points
                </button>
                <button className="w-full px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 text-sm" data-id="element-849">
                  View Rewards Catalog
                </button>
              </div>
            </div>
          </div>
          {/* Active Packages */}
          <div className="border border-gray-200 rounded-lg p-6" data-id="element-850">
            <div className="flex items-center justify-between mb-4" data-id="element-851">
              <div className="flex items-center space-x-2" data-id="element-852">
                <CreditCardIcon className="w-6 h-6 text-blue-600" data-id="element-853" />
                <h3 className="text-lg font-semibold text-gray-900" data-id="element-854">
                  Active Packages
                </h3>
              </div>
              <button className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm" data-id="element-855">
                Buy New Package
              </button>
            </div>
            <div className="space-y-4" data-id="element-856">
              {activePackages.map(pkg => <div key={pkg.id} className="border border-gray-200 rounded-lg p-4" data-id="element-857">
                  <div className="flex items-start justify-between mb-3" data-id="element-858">
                    <div data-id="element-859">
                      <h4 className="font-semibold text-gray-900" data-id="element-860">
                        {pkg.name}
                      </h4>
                      <div className="flex items-center space-x-4 text-sm text-gray-600 mt-1" data-id="element-861">
                        <span data-id="element-862">₹{pkg.price.toLocaleString()}</span>
                        <span className="px-2 py-1 bg-green-100 text-green-800 rounded-full text-xs font-medium" data-id="element-863">
                          {pkg.discount}% Discount
                        </span>
                      </div>
                    </div>
                    <span className="text-sm text-gray-600" data-id="element-864">
                      Valid until {pkg.validUntil}
                    </span>
                  </div>
                  <div className="mb-2" data-id="element-865">
                    <div className="flex justify-between text-sm mb-1" data-id="element-866">
                      <span className="text-gray-600" data-id="element-867">Services Used</span>
                      <span className="font-medium" data-id="element-868">
                        {pkg.servicesUsed} / {pkg.totalServices}
                      </span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2" data-id="element-869">
                      <div className="bg-blue-600 h-2 rounded-full" style={{
                  width: `${pkg.servicesUsed / pkg.totalServices * 100}%`
                }} data-id="element-870" />
                    </div>
                  </div>
                </div>)}
            </div>
          </div>
          {/* Available Discounts */}
          <div className="border border-gray-200 rounded-lg p-6" data-id="element-871">
            <div className="flex items-center space-x-2 mb-4" data-id="element-872">
              <TagIcon className="w-6 h-6 text-purple-600" data-id="element-873" />
              <h3 className="text-lg font-semibold text-gray-900" data-id="element-874">
                Available Discounts
              </h3>
            </div>
            <div className="space-y-3" data-id="element-875">
              {availableDiscounts.map(discount => <div key={discount.id} className="flex items-center justify-between p-4 border border-gray-200 rounded-lg" data-id="element-876">
                  <div className="flex-1" data-id="element-877">
                    <div className="flex items-center space-x-2 mb-1" data-id="element-878">
                      <h4 className="font-medium text-gray-900" data-id="element-879">
                        {discount.name}
                      </h4>
                      <span className="px-2 py-1 bg-purple-100 text-purple-800 text-xs font-medium rounded-full" data-id="element-880">
                        {discount.value}
                      </span>
                    </div>
                    <p className="text-sm text-gray-600" data-id="element-881">
                      Applicable to: {discount.applicable}
                    </p>
                  </div>
                  <span className={`px-3 py-1 text-xs font-medium rounded-full ${discount.status === 'Active' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`} data-id="element-882">
                    {discount.status}
                  </span>
                </div>)}
            </div>
          </div>
          {/* Billing Rules */}
          <div className="border border-gray-200 rounded-lg p-6" data-id="element-883">
            <h3 className="text-lg font-semibold text-gray-900 mb-4" data-id="element-884">
              Billing Rules & Benefits
            </h3>
            <div className="space-y-3" data-id="element-885">
              {billingRules.map(rule => <div key={rule.id} className="flex items-center justify-between p-3 border border-gray-200 rounded-lg" data-id="element-886">
                  <div className="flex-1" data-id="element-887">
                    <h4 className="font-medium text-gray-900" data-id="element-888">{rule.rule}</h4>
                    <p className="text-sm text-gray-600" data-id="element-889">
                      Discount: {rule.discount}
                    </p>
                  </div>
                  <span className="px-3 py-1 text-xs font-medium rounded-full bg-green-100 text-green-800" data-id="element-890">
                    {rule.status}
                  </span>
                </div>)}
            </div>
          </div>
          {/* Rewards History */}
          <div className="border border-gray-200 rounded-lg p-6" data-id="element-891">
            <h3 className="text-lg font-semibold text-gray-900 mb-4" data-id="element-892">
              Rewards History
            </h3>
            <div className="overflow-x-auto" data-id="element-893">
              <table className="w-full" data-id="element-894">
                <thead className="bg-gray-50 border-b border-gray-200" data-id="element-895">
                  <tr data-id="element-896">
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase" data-id="element-897">
                      Date
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase" data-id="element-898">
                      Action
                    </th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase" data-id="element-899">
                      Points
                    </th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase" data-id="element-900">
                      Balance
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200" data-id="element-901">
                  {rewardsHistory.map(reward => <tr key={reward.id} data-id="element-902">
                      <td className="px-4 py-3 text-sm text-gray-900" data-id="element-903">
                        {reward.date}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-700" data-id="element-904">
                        {reward.action}
                      </td>
                      <td className="px-4 py-3 text-sm text-right font-medium text-green-600" data-id="element-905">
                        +{reward.points}
                      </td>
                      <td className="px-4 py-3 text-sm text-right font-medium text-gray-900" data-id="element-906">
                        {reward.balance}
                      </td>
                    </tr>)}
                </tbody>
              </table>
            </div>
          </div>
        </>}
      {/* Billing & Checkout Section */}
      {activeSection === 'billing' && <>
          {/* Current Bill Summary */}
          <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg p-6 text-white" data-id="element-907">
            <div className="flex items-center justify-between mb-4" data-id="element-908">
              <div data-id="element-909">
                <h3 className="text-2xl font-bold" data-id="element-910">
                  ₹{currentBill.total.toLocaleString()}
                </h3>
                <p className="text-blue-100" data-id="element-911">Total Amount Due</p>
              </div>
              <div className="text-right" data-id="element-912">
                <p className="text-sm opacity-90" data-id="element-913">Bill Number</p>
                <p className="font-semibold" data-id="element-914">{currentBill.billNumber}</p>
                <p className="text-xs opacity-75 mt-1" data-id="element-915">{currentBill.date}</p>
              </div>
            </div>
            <button onClick={() => setShowPaymentModal(true)} className="w-full px-6 py-3 bg-white text-blue-600 rounded-lg hover:bg-blue-50 font-semibold" data-id="element-916">
              Proceed to Payment
            </button>
          </div>
          {/* Itemized Bill by Category */}
          <div className="border border-gray-200 rounded-lg p-6" data-id="element-917">
            <h3 className="text-lg font-semibold text-gray-900 mb-4" data-id="element-918">
              Itemized Bill
            </h3>
            <div className="overflow-x-auto" data-id="element-919">
              <table className="w-full" data-id="element-920">
                <thead className="bg-gray-50 border-b border-gray-200" data-id="element-921">
                  <tr data-id="element-922">
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase" data-id="element-923">
                      Category
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase" data-id="element-924">
                      Description
                    </th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase" data-id="element-925">
                      Qty
                    </th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase" data-id="element-926">
                      Rate
                    </th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase" data-id="element-927">
                      Amount
                    </th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase" data-id="element-928">
                      Tax
                    </th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase" data-id="element-929">
                      Total
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200" data-id="element-930">
                  {currentBill.items.map((item, index) => <tr key={index} className="hover:bg-gray-50" data-id="element-931">
                      <td className="px-4 py-3" data-id="element-932">
                        <span className={`px-2 py-1 text-xs font-medium rounded-full ${item.category === 'Consultation' ? 'bg-purple-100 text-purple-800' : item.category === 'Lab' ? 'bg-orange-100 text-orange-800' : item.category === 'Drugs' ? 'bg-green-100 text-green-800' : item.category === 'Consumables' ? 'bg-blue-100 text-blue-800' : item.category === 'Imaging' ? 'bg-pink-100 text-pink-800' : 'bg-gray-100 text-gray-800'}`} data-id="element-933">
                          {item.category}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-900" data-id="element-934">
                        {item.description}
                      </td>
                      <td className="px-4 py-3 text-sm text-center text-gray-700" data-id="element-935">
                        {item.quantity}
                      </td>
                      <td className="px-4 py-3 text-sm text-right text-gray-700" data-id="element-936">
                        ₹{item.rate}
                      </td>
                      <td className="px-4 py-3 text-sm text-right text-gray-900 font-medium" data-id="element-937">
                        ₹{item.amount}
                      </td>
                      <td className="px-4 py-3 text-sm text-right text-gray-600" data-id="element-938">
                        ₹{item.tax}
                      </td>
                      <td className="px-4 py-3 text-sm text-right text-gray-900 font-semibold" data-id="element-939">
                        ₹{item.amount + item.tax}
                      </td>
                    </tr>)}
                </tbody>
                <tfoot className="bg-gray-50 border-t-2 border-gray-300" data-id="element-940">
                  <tr data-id="element-941">
                    <td colSpan={4} className="px-4 py-3 text-right text-sm font-medium text-gray-700" data-id="element-942">
                      Subtotal:
                    </td>
                    <td className="px-4 py-3 text-right text-sm font-semibold text-gray-900" data-id="element-943">
                      ₹{currentBill.subtotal}
                    </td>
                    <td colSpan={2} data-id="element-944"></td>
                  </tr>
                  <tr data-id="element-945">
                    <td colSpan={4} className="px-4 py-3 text-right text-sm font-medium text-gray-700" data-id="element-946">
                      Discount (10%):
                    </td>
                    <td className="px-4 py-3 text-right text-sm font-semibold text-green-600" data-id="element-947">
                      -₹{currentBill.discount}
                    </td>
                    <td colSpan={2} data-id="element-948"></td>
                  </tr>
                  <tr data-id="element-949">
                    <td colSpan={4} className="px-4 py-3 text-right text-sm font-medium text-gray-700" data-id="element-950">
                      Tax (GST 18%):
                    </td>
                    <td className="px-4 py-3 text-right text-sm font-semibold text-gray-900" data-id="element-951">
                      ₹{currentBill.taxTotal}
                    </td>
                    <td colSpan={2} data-id="element-952"></td>
                  </tr>
                  <tr className="border-t-2 border-gray-300" data-id="element-953">
                    <td colSpan={4} className="px-4 py-4 text-right text-base font-bold text-gray-900" data-id="element-954">
                      Total Amount:
                    </td>
                    <td className="px-4 py-4 text-right text-lg font-bold text-blue-600" data-id="element-955">
                      ₹{currentBill.total}
                    </td>
                    <td colSpan={2} data-id="element-956"></td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>
          {/* Billing History */}
          <div className="border border-gray-200 rounded-lg p-6" data-id="element-957">
            <h3 className="text-lg font-semibold text-gray-900 mb-4" data-id="element-958">
              Billing History
            </h3>
            <div className="overflow-x-auto" data-id="element-959">
              <table className="w-full" data-id="element-960">
                <thead className="bg-gray-50 border-b border-gray-200" data-id="element-961">
                  <tr data-id="element-962">
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase" data-id="element-963">
                      Bill Number
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase" data-id="element-964">
                      Date
                    </th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase" data-id="element-965">
                      Amount
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase" data-id="element-966">
                      Payment Mode
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase" data-id="element-967">
                      Status
                    </th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase" data-id="element-968">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200" data-id="element-969">
                  {billingHistory.map(bill => <tr key={bill.id} className="hover:bg-gray-50" data-id="element-970">
                      <td className="px-4 py-3 text-sm font-medium text-gray-900" data-id="element-971">
                        {bill.billNumber}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-700" data-id="element-972">
                        {bill.date}
                      </td>
                      <td className="px-4 py-3 text-sm text-right font-medium text-gray-900" data-id="element-973">
                        ₹{bill.amount.toLocaleString()}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-700" data-id="element-974">
                        {bill.paymentMode}
                      </td>
                      <td className="px-4 py-3" data-id="element-975">
                        <span className={`px-2 py-1 text-xs font-medium rounded-full ${bill.status === 'Paid' ? 'bg-green-100 text-green-800' : bill.status === 'Pending' ? 'bg-yellow-100 text-yellow-800' : 'bg-red-100 text-red-800'}`} data-id="element-976">
                          {bill.status}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-center" data-id="element-977">
                        <button className="text-blue-600 hover:text-blue-700 text-sm font-medium" data-id="element-978">
                          <DownloadIcon className="w-4 h-4 inline mr-1" data-id="element-979" />
                          Download
                        </button>
                      </td>
                    </tr>)}
                </tbody>
              </table>
            </div>
          </div>
        </>}
      {/* Insurance Claims Section */}
      {activeSection === 'insurance' && <>
          {/* Insurance Info Card */}
          <div className="bg-gradient-to-br from-purple-500 to-purple-600 rounded-lg p-6 text-white" data-id="element-980">
            <div className="flex items-center space-x-3 mb-4" data-id="element-981">
              <ShieldIcon className="w-8 h-8" data-id="element-982" />
              <div data-id="element-983">
                <h3 className="text-xl font-bold" data-id="element-984">Blue Cross Blue Shield</h3>
                <p className="text-purple-100" data-id="element-985">Policy: BC123456789</p>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4 text-sm" data-id="element-986">
              <div data-id="element-987">
                <p className="text-purple-100" data-id="element-988">Group Number</p>
                <p className="font-semibold" data-id="element-989">GRP001</p>
              </div>
              <div data-id="element-990">
                <p className="text-purple-100" data-id="element-991">Valid Until</p>
                <p className="font-semibold" data-id="element-992">2025-12-31</p>
              </div>
            </div>
          </div>
          {/* Initiate New Claim */}
          <div className="border border-gray-200 rounded-lg p-6" data-id="element-993">
            <h3 className="text-lg font-semibold text-gray-900 mb-4" data-id="element-994">
              Initiate Insurance Claim
            </h3>
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4" data-id="element-995">
              <div className="flex items-start space-x-3" data-id="element-996">
                <AlertCircleIcon className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" data-id="element-997" />
                <div className="text-sm text-blue-900" data-id="element-998">
                  <p className="font-medium mb-1" data-id="element-999">
                    Current Bill Eligible for Insurance Claim
                  </p>
                  <p data-id="element-1000">
                    Bill {currentBill.billNumber} - ₹
                    {currentBill.total.toLocaleString()} can be submitted to
                    your insurance provider.
                  </p>
                </div>
              </div>
            </div>
            <div className="space-y-4" data-id="element-1001">
              <div data-id="element-1002">
                <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-1003">
                  Select Bill for Claim
                </label>
                <select className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent" data-id="element-1004">
                  <option value={currentBill.billNumber} data-id="element-1005">
                    {currentBill.billNumber} - ₹
                    {currentBill.total.toLocaleString()}
                  </option>
                  <option value="INV-2024-0142" data-id="element-1006">INV-2024-0142 - ₹3,250</option>
                </select>
              </div>
              <div data-id="element-1007">
                <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-1008">
                  Insurance Provider Format
                </label>
                <select className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent" data-id="element-1009">
                  <option value="bcbs" data-id="element-1010">
                    Blue Cross Blue Shield (Standard Format)
                  </option>
                  <option value="uhc" data-id="element-1011">United Healthcare (UHC Format)</option>
                  <option value="aetna" data-id="element-1012">Aetna (Claims Portal Format)</option>
                  <option value="cigna" data-id="element-1013">Cigna (Electronic Claim Format)</option>
                  <option value="generic" data-id="element-1014">Generic Insurance Format</option>
                </select>
              </div>
              <div data-id="element-1015">
                <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-1016">
                  Additional Documents
                </label>
                <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center" data-id="element-1017">
                  <p className="text-sm text-gray-600 mb-2" data-id="element-1018">
                    Upload medical reports, prescriptions, or lab results
                  </p>
                  <button className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 text-sm" data-id="element-1019">
                    Choose Files
                  </button>
                </div>
              </div>
              <button onClick={handleInitiateInsuranceClaim} className="w-full px-6 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 font-semibold" data-id="element-1020">
                Submit Insurance Claim
              </button>
            </div>
          </div>
          {/* Claims History */}
          <div className="border border-gray-200 rounded-lg p-6" data-id="element-1021">
            <h3 className="text-lg font-semibold text-gray-900 mb-4" data-id="element-1022">
              Claims History
            </h3>
            <div className="space-y-4" data-id="element-1023">
              {insuranceClaims.map(claim => <div key={claim.id} className="border border-gray-200 rounded-lg p-4" data-id="element-1024">
                  <div className="flex items-start justify-between mb-3" data-id="element-1025">
                    <div data-id="element-1026">
                      <h4 className="font-semibold text-gray-900" data-id="element-1027">
                        {claim.claimNumber}
                      </h4>
                      <p className="text-sm text-gray-600" data-id="element-1028">
                        Bill: {claim.billNumber}
                      </p>
                      <p className="text-sm text-gray-600" data-id="element-1029">{claim.provider}</p>
                    </div>
                    <span className={`px-3 py-1 text-xs font-medium rounded-full ${claim.status === 'Approved' ? 'bg-green-100 text-green-800' : claim.status === 'Processing' ? 'bg-yellow-100 text-yellow-800' : 'bg-red-100 text-red-800'}`} data-id="element-1030">
                      {claim.status}
                    </span>
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm" data-id="element-1031">
                    <div data-id="element-1032">
                      <p className="text-gray-600" data-id="element-1033">Claim Date</p>
                      <p className="font-medium text-gray-900" data-id="element-1034">{claim.date}</p>
                    </div>
                    <div data-id="element-1035">
                      <p className="text-gray-600" data-id="element-1036">Bill Amount</p>
                      <p className="font-medium text-gray-900" data-id="element-1037">
                        ₹{claim.amount.toLocaleString()}
                      </p>
                    </div>
                    <div data-id="element-1038">
                      <p className="text-gray-600" data-id="element-1039">Claimed Amount</p>
                      <p className="font-medium text-green-600" data-id="element-1040">
                        ₹{claim.claimedAmount.toLocaleString()}
                      </p>
                    </div>
                    <div data-id="element-1041">
                      <p className="text-gray-600" data-id="element-1042">Approval Date</p>
                      <p className="font-medium text-gray-900" data-id="element-1043">
                        {claim.approvalDate}
                      </p>
                    </div>
                  </div>
                  {claim.status === 'Approved' && <div className="mt-3 pt-3 border-t border-gray-200" data-id="element-1044">
                      <button className="text-blue-600 hover:text-blue-700 text-sm font-medium" data-id="element-1045">
                        <DownloadIcon className="w-4 h-4 inline mr-1" data-id="element-1046" />
                        Download Approval Letter
                      </button>
                    </div>}
                </div>)}
            </div>
          </div>
        </>}
      {/* Payments & Refunds Section */}
      {activeSection === 'payments' && <>
          {/* Payment Modes */}
          <div className="border border-gray-200 rounded-lg p-6" data-id="element-1047">
            <h3 className="text-lg font-semibold text-gray-900 mb-4" data-id="element-1048">
              Available Payment Methods
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-id="element-1049">
              {paymentModes.map(mode => {
            const Icon = mode.icon;
            return <div key={mode.id} className="border border-gray-200 rounded-lg p-4 hover:border-blue-500 hover:shadow-md transition-all cursor-pointer" data-id="element-1050">
                    <div className="flex items-center space-x-3" data-id="element-1051">
                      <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center" data-id="element-1052">
                        <Icon className="w-6 h-6 text-blue-600" data-id="element-1053" />
                      </div>
                      <div data-id="element-1054">
                        <h4 className="font-medium text-gray-900" data-id="element-1055">
                          {mode.name}
                        </h4>
                        <p className="text-xs text-gray-500" data-id="element-1056">Fast & Secure</p>
                      </div>
                    </div>
                  </div>;
          })}
            </div>
          </div>
          {/* Refunds & Credit Notes */}
          <div className="border border-gray-200 rounded-lg p-6" data-id="element-1057">
            <h3 className="text-lg font-semibold text-gray-900 mb-4" data-id="element-1058">
              Refunds & Credit Notes
            </h3>
            <div className="space-y-3" data-id="element-1059">
              {refundsCreditNotes.map(item => <div key={item.id} className="border border-gray-200 rounded-lg p-4" data-id="element-1060">
                  <div className="flex items-start justify-between mb-2" data-id="element-1061">
                    <div className="flex items-center space-x-2" data-id="element-1062">
                      <RefreshCwIcon className={`w-5 h-5 ${item.type === 'Refund' ? 'text-red-600' : 'text-blue-600'}`} data-id="element-1063" />
                      <div data-id="element-1064">
                        <h4 className="font-semibold text-gray-900" data-id="element-1065">
                          {item.referenceNumber}
                        </h4>
                        <p className="text-sm text-gray-600" data-id="element-1066">{item.type}</p>
                      </div>
                    </div>
                    <span className={`px-3 py-1 text-xs font-medium rounded-full ${item.status === 'Completed' ? 'bg-green-100 text-green-800' : item.status === 'Applied' ? 'bg-blue-100 text-blue-800' : 'bg-yellow-100 text-yellow-800'}`} data-id="element-1067">
                      {item.status}
                    </span>
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm" data-id="element-1068">
                    <div data-id="element-1069">
                      <p className="text-gray-600" data-id="element-1070">Bill Number</p>
                      <p className="font-medium text-gray-900" data-id="element-1071">
                        {item.billNumber}
                      </p>
                    </div>
                    <div data-id="element-1072">
                      <p className="text-gray-600" data-id="element-1073">Date</p>
                      <p className="font-medium text-gray-900" data-id="element-1074">{item.date}</p>
                    </div>
                    <div data-id="element-1075">
                      <p className="text-gray-600" data-id="element-1076">Amount</p>
                      <p className="font-medium text-gray-900" data-id="element-1077">
                        ₹{item.amount.toLocaleString()}
                      </p>
                    </div>
                    <div data-id="element-1078">
                      <p className="text-gray-600" data-id="element-1079">Reason</p>
                      <p className="font-medium text-gray-900" data-id="element-1080">{item.reason}</p>
                    </div>
                  </div>
                </div>)}
            </div>
            <button className="mt-4 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 text-sm" data-id="element-1081">
              Request Refund
            </button>
          </div>
          {/* Deferred Payments */}
          <div className="border border-gray-200 rounded-lg p-6" data-id="element-1082">
            <h3 className="text-lg font-semibold text-gray-900 mb-4" data-id="element-1083">
              Deferred Payments
            </h3>
            <div className="space-y-3" data-id="element-1084">
              {deferredPayments.map(payment => <div key={payment.id} className="border border-gray-200 rounded-lg p-4" data-id="element-1085">
                  <div className="flex items-start justify-between mb-3" data-id="element-1086">
                    <div className="flex items-center space-x-2" data-id="element-1087">
                      <ClockIcon className="w-5 h-5 text-orange-600" data-id="element-1088" />
                      <div data-id="element-1089">
                        <h4 className="font-semibold text-gray-900" data-id="element-1090">
                          {payment.billNumber}
                        </h4>
                        <p className="text-sm text-gray-600" data-id="element-1091">
                          Payment Plan Active
                        </p>
                      </div>
                    </div>
                    <span className="px-3 py-1 text-xs font-medium rounded-full bg-orange-100 text-orange-800" data-id="element-1092">
                      {payment.status}
                    </span>
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm mb-3" data-id="element-1093">
                    <div data-id="element-1094">
                      <p className="text-gray-600" data-id="element-1095">Original Amount</p>
                      <p className="font-medium text-gray-900" data-id="element-1096">
                        ₹{payment.originalAmount.toLocaleString()}
                      </p>
                    </div>
                    <div data-id="element-1097">
                      <p className="text-gray-600" data-id="element-1098">Paid Amount</p>
                      <p className="font-medium text-green-600" data-id="element-1099">
                        ₹{payment.paidAmount.toLocaleString()}
                      </p>
                    </div>
                    <div data-id="element-1100">
                      <p className="text-gray-600" data-id="element-1101">Remaining</p>
                      <p className="font-medium text-red-600" data-id="element-1102">
                        ₹{payment.remainingAmount.toLocaleString()}
                      </p>
                    </div>
                    <div data-id="element-1103">
                      <p className="text-gray-600" data-id="element-1104">Due Date</p>
                      <p className="font-medium text-gray-900" data-id="element-1105">
                        {payment.dueDate}
                      </p>
                    </div>
                  </div>
                  <div className="mb-2" data-id="element-1106">
                    <div className="flex justify-between text-sm mb-1" data-id="element-1107">
                      <span className="text-gray-600" data-id="element-1108">Payment Progress</span>
                      <span className="font-medium" data-id="element-1109">
                        {Math.round(payment.paidAmount / payment.originalAmount * 100)}
                        %
                      </span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2" data-id="element-1110">
                      <div className="bg-green-600 h-2 rounded-full" style={{
                  width: `${payment.paidAmount / payment.originalAmount * 100}%`
                }} data-id="element-1111" />
                    </div>
                  </div>
                  <button className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 text-sm" data-id="element-1112">
                    Make Payment
                  </button>
                </div>)}
            </div>
          </div>
        </>}
      {/* Invoices & Tax Section */}
      {activeSection === 'invoices' && <>
          {/* Tax Summary */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-id="element-1113">
            <div className="bg-white border border-gray-200 rounded-lg p-6" data-id="element-1114">
              <div className="flex items-center space-x-2 mb-2" data-id="element-1115">
                <FileTextIcon className="w-5 h-5 text-blue-600" data-id="element-1116" />
                <span className="text-sm text-gray-600" data-id="element-1117">Total Invoices</span>
              </div>
              <p className="text-2xl font-bold text-gray-900" data-id="element-1118">24</p>
              <p className="text-xs text-gray-500 mt-1" data-id="element-1119">This Year</p>
            </div>
            <div className="bg-white border border-gray-200 rounded-lg p-6" data-id="element-1120">
              <div className="flex items-center space-x-2 mb-2" data-id="element-1121">
                <DollarSignIcon className="w-5 h-5 text-green-600" data-id="element-1122" />
                <span className="text-sm text-gray-600" data-id="element-1123">Total Tax Paid</span>
              </div>
              <p className="text-2xl font-bold text-gray-900" data-id="element-1124">₹8,456</p>
              <p className="text-xs text-gray-500 mt-1" data-id="element-1125">GST 18%</p>
            </div>
            <div className="bg-white border border-gray-200 rounded-lg p-6" data-id="element-1126">
              <div className="flex items-center space-x-2 mb-2" data-id="element-1127">
                <CheckCircleIcon className="w-5 h-5 text-purple-600" data-id="element-1128" />
                <span className="text-sm text-gray-600" data-id="element-1129">Compliance Status</span>
              </div>
              <p className="text-2xl font-bold text-green-600" data-id="element-1130">Active</p>
              <p className="text-xs text-gray-500 mt-1" data-id="element-1131">All taxes filed</p>
            </div>
          </div>
          {/* Invoice Management */}
          <div className="border border-gray-200 rounded-lg p-6" data-id="element-1132">
            <h3 className="text-lg font-semibold text-gray-900 mb-4" data-id="element-1133">
              Invoice Management
            </h3>
            <div className="overflow-x-auto" data-id="element-1134">
              <table className="w-full" data-id="element-1135">
                <thead className="bg-gray-50 border-b border-gray-200" data-id="element-1136">
                  <tr data-id="element-1137">
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase" data-id="element-1138">
                      Invoice Number
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase" data-id="element-1139">
                      Date
                    </th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase" data-id="element-1140">
                      Subtotal
                    </th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase" data-id="element-1141">
                      Tax (GST)
                    </th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase" data-id="element-1142">
                      Total
                    </th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase" data-id="element-1143">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200" data-id="element-1144">
                  {billingHistory.slice(0, 5).map(bill => {
                const subtotal = Math.round(bill.amount / 1.18);
                const tax = bill.amount - subtotal;
                return <tr key={bill.id} className="hover:bg-gray-50" data-id="element-1145">
                        <td className="px-4 py-3 text-sm font-medium text-gray-900" data-id="element-1146">
                          {bill.billNumber}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-700" data-id="element-1147">
                          {bill.date}
                        </td>
                        <td className="px-4 py-3 text-sm text-right text-gray-900" data-id="element-1148">
                          ₹{subtotal.toLocaleString()}
                        </td>
                        <td className="px-4 py-3 text-sm text-right text-gray-600" data-id="element-1149">
                          ₹{tax.toLocaleString()}
                        </td>
                        <td className="px-4 py-3 text-sm text-right font-medium text-gray-900" data-id="element-1150">
                          ₹{bill.amount.toLocaleString()}
                        </td>
                        <td className="px-4 py-3 text-center" data-id="element-1151">
                          <div className="flex items-center justify-center space-x-2" data-id="element-1152">
                            <button className="text-blue-600 hover:text-blue-700 text-sm" data-id="element-1153">
                              <DownloadIcon className="w-4 h-4" data-id="element-1154" />
                            </button>
                            <button className="text-gray-600 hover:text-gray-700 text-sm" data-id="element-1155">
                              View
                            </button>
                          </div>
                        </td>
                      </tr>;
              })}
                </tbody>
              </table>
            </div>
          </div>
          {/* Tax Compliance */}
          <div className="border border-gray-200 rounded-lg p-6" data-id="element-1156">
            <h3 className="text-lg font-semibold text-gray-900 mb-4" data-id="element-1157">
              Tax & Regulatory Compliance
            </h3>
            <div className="space-y-4" data-id="element-1158">
              <div className="bg-green-50 border border-green-200 rounded-lg p-4" data-id="element-1159">
                <div className="flex items-start space-x-3" data-id="element-1160">
                  <CheckCircleIcon className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" data-id="element-1161" />
                  <div className="flex-1" data-id="element-1162">
                    <h4 className="font-medium text-green-900 mb-1" data-id="element-1163">
                      GST Compliance Active
                    </h4>
                    <p className="text-sm text-green-800" data-id="element-1164">
                      All invoices are GST compliant with 18% tax rate applied
                    </p>
                    <div className="mt-2 text-xs text-green-700" data-id="element-1165">
                      <p data-id="element-1166">GSTIN: 29ABCDE1234F1Z5</p>
                      <p data-id="element-1167">Last Filed: 2024-01-10</p>
                    </div>
                  </div>
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-1168">
                <div className="border border-gray-200 rounded-lg p-4" data-id="element-1169">
                  <h4 className="font-medium text-gray-900 mb-2" data-id="element-1170">
                    Invoice Format
                  </h4>
                  <p className="text-sm text-gray-600 mb-3" data-id="element-1171">
                    Standard GST compliant format with all required fields
                  </p>
                  <button className="text-blue-600 hover:text-blue-700 text-sm font-medium" data-id="element-1172">
                    View Sample Invoice
                  </button>
                </div>
                <div className="border border-gray-200 rounded-lg p-4" data-id="element-1173">
                  <h4 className="font-medium text-gray-900 mb-2" data-id="element-1174">
                    Tax Reports
                  </h4>
                  <p className="text-sm text-gray-600 mb-3" data-id="element-1175">
                    Download monthly and annual tax reports
                  </p>
                  <button className="text-blue-600 hover:text-blue-700 text-sm font-medium" data-id="element-1176">
                    <DownloadIcon className="w-4 h-4 inline mr-1" data-id="element-1177" />
                    Download Reports
                  </button>
                </div>
              </div>
              <div className="border border-gray-200 rounded-lg p-4" data-id="element-1178">
                <h4 className="font-medium text-gray-900 mb-3" data-id="element-1179">
                  Regulatory Documents
                </h4>
                <div className="space-y-2" data-id="element-1180">
                  <div className="flex items-center justify-between p-2 hover:bg-gray-50 rounded" data-id="element-1181">
                    <span className="text-sm text-gray-700" data-id="element-1182">
                      GST Registration Certificate
                    </span>
                    <button className="text-blue-600 hover:text-blue-700 text-sm" data-id="element-1183">
                      <DownloadIcon className="w-4 h-4 inline" data-id="element-1184" />
                    </button>
                  </div>
                  <div className="flex items-center justify-between p-2 hover:bg-gray-50 rounded" data-id="element-1185">
                    <span className="text-sm text-gray-700" data-id="element-1186">
                      Annual Tax Returns (2023)
                    </span>
                    <button className="text-blue-600 hover:text-blue-700 text-sm" data-id="element-1187">
                      <DownloadIcon className="w-4 h-4 inline" data-id="element-1188" />
                    </button>
                  </div>
                  <div className="flex items-center justify-between p-2 hover:bg-gray-50 rounded" data-id="element-1189">
                    <span className="text-sm text-gray-700" data-id="element-1190">
                      Monthly GST Returns
                    </span>
                    <button className="text-blue-600 hover:text-blue-700 text-sm" data-id="element-1191">
                      <DownloadIcon className="w-4 h-4 inline" data-id="element-1192" />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </>}
      {/* Payment Modal */}
      {showPaymentModal && <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" data-id="element-1193">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto" data-id="element-1194">
            <div className="p-6 border-b border-gray-200" data-id="element-1195">
              <h2 className="text-2xl font-bold text-gray-900" data-id="element-1196">
                Complete Payment
              </h2>
              <p className="text-gray-600 mt-1" data-id="element-1197">
                Bill: {currentBill.billNumber}
              </p>
            </div>
            <div className="p-6 space-y-6" data-id="element-1198">
              {/* Amount Summary */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4" data-id="element-1199">
                <div className="flex items-center justify-between" data-id="element-1200">
                  <span className="text-gray-700" data-id="element-1201">Total Amount</span>
                  <span className="text-2xl font-bold text-blue-600" data-id="element-1202">
                    ₹{currentBill.total.toLocaleString()}
                  </span>
                </div>
              </div>
              {/* Payment Mode Selection */}
              <div data-id="element-1203">
                <label className="block text-sm font-medium text-gray-700 mb-3" data-id="element-1204">
                  Select Payment Mode
                </label>
                <div className="grid grid-cols-2 gap-3" data-id="element-1205">
                  {paymentModes.map(mode => {
                const Icon = mode.icon;
                return <button key={mode.id} onClick={() => setSelectedPaymentMode(mode.id)} className={`p-4 border-2 rounded-lg transition-all ${selectedPaymentMode === mode.id ? 'border-blue-600 bg-blue-50' : 'border-gray-200 hover:border-gray-300'}`} data-id="element-1206">
                        <Icon className="w-8 h-8 text-blue-600 mx-auto mb-2" data-id="element-1207" />
                        <p className="text-sm font-medium text-gray-900" data-id="element-1208">
                          {mode.name}
                        </p>
                      </button>;
              })}
                </div>
              </div>
              {/* Payment Details Form */}
              {selectedPaymentMode && selectedPaymentMode !== 'invoice' && <div className="space-y-4" data-id="element-1209">
                  <h3 className="font-medium text-gray-900" data-id="element-1210">Payment Details</h3>
                  {selectedPaymentMode === 'card' && <>
                      <input type="text" placeholder="Card Number" className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent" data-id="element-1211" />
                      <div className="grid grid-cols-2 gap-4" data-id="element-1212">
                        <input type="text" placeholder="MM/YY" className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent" data-id="element-1213" />
                        <input type="text" placeholder="CVV" className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent" data-id="element-1214" />
                      </div>
                    </>}
                  {selectedPaymentMode === 'upi' && <input type="text" placeholder="UPI ID (e.g., name@upi)" className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent" data-id="element-1215" />}
                  {selectedPaymentMode === 'bank' && <>
                      <input type="text" placeholder="Account Number" className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent" data-id="element-1216" />
                      <input type="text" placeholder="IFSC Code" className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent" data-id="element-1217" />
                    </>}
                </div>}
            </div>
            <div className="p-6 border-t border-gray-200 flex justify-end space-x-3" data-id="element-1218">
              <button onClick={() => setShowPaymentModal(false)} className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50" data-id="element-1219">
                Cancel
              </button>
              <button onClick={handleProcessPayment} className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-semibold" data-id="element-1220">
                {selectedPaymentMode === 'invoice' ? 'Generate Invoice' : `Pay ₹${currentBill.total.toLocaleString()}`}
              </button>
            </div>
          </div>
        </div>}
    </div>;
}